import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { reportingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { exportReport } from '../utils/reportExport';
import '../styles/ReportsDashboard.css';

interface SystemWideStats {
  total_quizzes: number;
  total_responses: number;
  total_completed_responses: number;
  total_participants: number;
  average_quiz_score: number;
  most_popular_category?: string;
  busiest_day?: string;
  quiz_performance_by_category: CategoryPerformance[];
  recent_activity: RecentActivity[];
}

interface CategoryPerformance {
  category: string;
  quiz_count: number;
  total_responses: number;
  average_score: number;
}

interface RecentActivity {
  date: string;
  activity_type: string;
  description: string;
  quiz_title?: string;
  participant_name?: string;
}

interface AvailableQuiz {
  id: number;
  title: string;
  category?: string;
  total_responses: number;
  completed_responses: number;
  has_data: boolean;
}

interface TimeRange {
  period?: 'week' | 'month' | 'quarter' | 'year' | 'all';
  start_date?: string;
  end_date?: string;
}

interface OverallQuestionPerformance {
  total_questions_answered: number;
  total_correct: number;
  total_incorrect: number;
  total_partial: number;
  correct_percentage: number;
  incorrect_percentage: number;
  partial_percentage: number;
  topic_performance: TopicPerformance[];
}

interface TopicPerformance {
  category: string;
  total_questions_answered: number;
  correct_answers: number;
  incorrect_answers: number;
  partial_answers: number;
  correct_percentage: number;
  incorrect_percentage: number;
  partial_percentage: number;
  average_accuracy: number;
  difficulty_level: 'easy' | 'moderate' | 'difficult';
}

interface QuizQuestionPerformance {
  quiz_id: number;
  quiz_title: string;
  quiz_category?: string;
  total_questions_answered: number;
  total_correct: number;
  total_incorrect: number;
  total_partial: number;
  correct_percentage: number;
  incorrect_percentage: number;
  partial_percentage: number;
  question_performance: QuestionPerformanceDetail[];
}

interface QuestionPerformanceDetail {
  question_id: number;
  question_text: string;
  question_type: string;
  question_order: number;
  total_answers: number;
  correct_answers: number;
  incorrect_answers: number;
  partial_answers: number;
  correct_percentage: number;
  incorrect_percentage: number;
  partial_percentage: number;
  average_accuracy: number;
  difficulty_level: 'easy' | 'moderate' | 'difficult';
}

const ReportsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [systemStats, setSystemStats] = useState<SystemWideStats | null>(null);
  const [availableQuizzes, setAvailableQuizzes] = useState<AvailableQuiz[]>([]);
  
  // Initialize state from URL params
  const [selectedQuiz, setSelectedQuiz] = useState<number | null>(() => {
    const quizId = searchParams.get('quizId');
    return quizId ? parseInt(quizId, 10) : null;
  });
  const [timeRange, setTimeRange] = useState<TimeRange>({ period: 'month' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'quiz' | 'categories' | 'performance'>(
    (searchParams.get('tab') as 'overview' | 'quiz' | 'categories' | 'performance') || 'overview'
  );
  const [questionPerformance, setQuestionPerformance] = useState<OverallQuestionPerformance | null>(null);
  
  // Track if this is the initial mount
  const isInitialMount = useRef(true);
  // Track previous searchParams to detect browser navigation
  const prevSearchParamsRef = useRef<string>(searchParams.toString());

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Sync state FROM URL params when they change (browser back/forward)
  useEffect(() => {
    const currentParamsString = searchParams.toString();
    const prevParamsString = prevSearchParamsRef.current;
    
    // Only sync if URL params actually changed (browser navigation)
    if (currentParamsString !== prevParamsString) {
      prevSearchParamsRef.current = currentParamsString;
      
      const urlTab = (searchParams.get('tab') as 'overview' | 'quiz' | 'categories' | 'performance') || 'overview';
      const urlQuizId = searchParams.get('quizId');
      const urlSelectedQuiz = urlQuizId ? parseInt(urlQuizId, 10) : null;
      
      // Only update state if it differs from URL (browser back/forward navigation)
      if (urlTab !== activeTab) {
        setActiveTab(urlTab);
      }
      if (urlSelectedQuiz !== selectedQuiz) {
        setSelectedQuiz(urlSelectedQuiz);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Sync URL params when state changes (user actions)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevSearchParamsRef.current = searchParams.toString();
      return;
    }

    const params = new URLSearchParams();
    if (activeTab !== 'overview') params.set('tab', activeTab);
    if (selectedQuiz) params.set('quizId', selectedQuiz.toString());
    
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    
    // Only update if params actually changed
    if (currentParamsString !== newParamsString) {
      prevSearchParamsRef.current = newParamsString;
      setSearchParams(params, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedQuiz]);

  useEffect(() => {
    if (isAdmin) {
      loadSystemStats();
      loadAvailableQuizzes();
      if (activeTab === 'performance') {
        loadQuestionPerformance();
      }
    }
  }, [isAdmin, timeRange, activeTab]);

  const loadSystemStats = async () => {
    try {
      setLoading(true);
      const response = await reportingAPI.getSystemOverview(timeRange);
      setSystemStats(response.data.data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading system stats:', err);
      setError(err.response?.data?.message || 'Failed to load system statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableQuizzes = async () => {
    try {
      const response = await reportingAPI.getAvailableQuizzes();
      setAvailableQuizzes(response.data.data);
    } catch (err: any) {
      console.error('Error loading available quizzes:', err);
    }
  };

  const loadQuestionPerformance = async () => {
    try {
      setLoading(true);
      const response = await reportingAPI.getOverallQuestionPerformance(timeRange);
      setQuestionPerformance(response.data.data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading question performance:', err);
      setError(err.response?.data?.message || 'Failed to load question performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (newTimeRange: TimeRange) => {
    setTimeRange(newTimeRange);
  };

  const handleExportPDF = async (reportType: string, quizId?: number) => {
    try {
      setLoading(true);
      await exportReport({
        reportType,
        quizId,
        format: 'pdf',
        timeRange
      });
      setError(null);
    } catch (err: any) {
      console.error('Error exporting PDF:', err);
      setError(err.response?.data?.message || 'Failed to export PDF');
    } finally {
      setLoading(false);
    }
  };

  // Legacy function kept for backward compatibility (not used anymore)
  const generatePDFContent = (reportType: string, data: any, reportName: string): string => {
    const currentDate = new Date().toLocaleDateString();
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .section { margin-bottom: 25px; }
          .section h2 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          .stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 20px; }
          .stat-card { border: 1px solid #ddd; padding: 15px; text-align: center; }
          .stat-value { font-size: 24px; font-weight: bold; color: #007bff; }
          .stat-label { color: #666; font-size: 12px; margin-top: 5px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${reportName}</h1>
          <p>Generated on ${currentDate}</p>
        </div>
    `;
    
    if (reportType === 'system_wide') {
      html += `
        <div class="section">
          <div class="stat-grid">
            <div class="stat-card">
              <div class="stat-value">${data.total_quizzes}</div>
              <div class="stat-label">Total Quizzes</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${data.total_responses}</div>
              <div class="stat-label">Total Responses</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${data.total_completed_responses}</div>
              <div class="stat-label">Completed Responses</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${data.total_participants}</div>
              <div class="stat-label">Unique Participants</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${Math.round(data.average_quiz_score)}%</div>
              <div class="stat-label">Average Score</div>
            </div>
          </div>
        </div>
      `;
    } else if (reportType === 'quiz_detailed') {
      html += `
        <div class="section">
          <h2>${data.quiz_title}</h2>
          <div class="stat-grid">
            <div class="stat-card">
              <div class="stat-value">${data.total_responses}</div>
              <div class="stat-label">Total Responses</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${data.completed_responses}</div>
              <div class="stat-label">Completed</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${Math.round(data.average_score || 0)}%</div>
              <div class="stat-label">Average Score</div>
            </div>
          </div>
        </div>
      `;
    }
    
    html += '</body></html>';
    return html;
  };

  if (!isAdmin) {
    return (
      <div className="reports-dashboard">
        <div className="unauthorized-message">
          <h2>Access Denied</h2>
          <p>You need admin privileges to access the reports dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-dashboard">
      <div className="dashboard-header">
        <h1>Reports Dashboard</h1>
        <p>Comprehensive analytics and insights for your quiz system</p>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="dashboard-controls">
        <div className="time-range-selector">
          <label>Time Range:</label>
          <select 
            value={timeRange.period || 'custom'} 
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setTimeRange({});
              } else {
                setTimeRange({ period: e.target.value as any });
              }
            }}
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {!timeRange.period && (
          <div className="custom-date-range">
            <input
              type="date"
              placeholder="Start Date"
              value={timeRange.start_date || ''}
              onChange={(e) => setTimeRange({...timeRange, start_date: e.target.value})}
            />
            <input
              type="date"
              placeholder="End Date"
              value={timeRange.end_date || ''}
              onChange={(e) => setTimeRange({...timeRange, end_date: e.target.value})}
            />
          </div>
        )}
      </div>

      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          System Overview
        </button>
        <button 
          className={`tab ${activeTab === 'quiz' ? 'active' : ''}`}
          onClick={() => setActiveTab('quiz')}
        >
          Quiz Reports
        </button>
        <button 
          className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </button>
        <button 
          className={`tab ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          Question Performance
        </button>
      </div>

      <div className="dashboard-content">
        {loading && (
          <div className="loading-spinner">
            <div className="spinner-circle"></div>
            <div className="spinner-text">Loading reports...</div>
          </div>
        )}

        {activeTab === 'overview' && systemStats && (
          <div key="overview" className="tab-content">
            <SystemOverviewTab 
              stats={systemStats} 
              onExport={() => handleExportPDF('system_wide')}
            />
          </div>
        )}

        {activeTab === 'quiz' && (
          <div key="quiz" className="tab-content">
            <QuizReportsTab 
              availableQuizzes={availableQuizzes}
              selectedQuiz={selectedQuiz}
              onQuizSelect={setSelectedQuiz}
              timeRange={timeRange}
              onExport={handleExportPDF}
            />
          </div>
        )}

        {activeTab === 'categories' && systemStats && (
          <div key="categories" className="tab-content">
            <CategoryPerformanceTab 
              categories={systemStats.quiz_performance_by_category}
              onExport={() => handleExportPDF('category_performance')}
            />
          </div>
        )}

        {activeTab === 'performance' && questionPerformance && (
          <div key="performance" className="tab-content">
            <QuestionPerformanceTab 
              performance={questionPerformance}
              onExport={() => handleExportPDF('overall_question_performance')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// System Overview Tab Component
const SystemOverviewTab: React.FC<{ 
  stats: SystemWideStats; 
  onExport: (reportType: string, quizId?: number) => void 
}> = ({ stats, onExport }) => (
  <div className="overview-tab">
    <div className="stats-grid">
      <div className="stat-card">
        <div className="stat-value">{stats.total_quizzes}</div>
        <div className="stat-label">Total Quizzes</div>
      </div>
      <div className="stat-card">
        <div className="stat-value">{stats.total_responses}</div>
        <div className="stat-label">Total Responses</div>
      </div>
      <div className="stat-card">
        <div className="stat-value">{stats.total_completed_responses}</div>
        <div className="stat-label">Completed Responses</div>
      </div>
      <div className="stat-card">
        <div className="stat-value">{stats.total_participants}</div>
        <div className="stat-label">Unique Participants</div>
      </div>
      <div className="stat-card">
        <div className="stat-value">{Math.round(stats.average_quiz_score)}%</div>
        <div className="stat-label">Average Score</div>
      </div>
      <div className="stat-card">
        <div className="stat-value">{stats.most_popular_category || 'N/A'}</div>
        <div className="stat-label">Most Popular Category</div>
      </div>
    </div>

    <div className="charts-section">
      <div className="chart-container">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {stats.recent_activity.slice(0, 10).map((activity, index) => (
            <div key={index} className="activity-item">
              <div className="activity-date">
                {new Date(activity.date).toLocaleDateString()}
              </div>
              <div className="activity-description">{activity.description}</div>
              {activity.quiz_title && (
                <div className="activity-quiz">Quiz: {activity.quiz_title}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="actions">
      <button className="export-btn" onClick={() => onExport('system_wide')}>
        Export System Report (PDF)
      </button>
    </div>
  </div>
);

// Quiz Reports Tab Component
const QuizReportsTab: React.FC<{
  availableQuizzes: AvailableQuiz[];
  selectedQuiz: number | null;
  onQuizSelect: (quizId: number | null) => void;
  timeRange: TimeRange;
  onExport: (reportType: string, quizId?: number) => void;
}> = ({ availableQuizzes, selectedQuiz, onQuizSelect, timeRange, onExport }) => {
  const [quizReport, setQuizReport] = useState<any>(null);
  const [quizQuestionPerformance, setQuizQuestionPerformance] = useState<QuizQuestionPerformance | null>(null);
  const [showQuestionPerformance, setShowQuestionPerformance] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingQuestionPerformance, setLoadingQuestionPerformance] = useState(false);

  useEffect(() => {
    if (selectedQuiz) {
      loadQuizReport(selectedQuiz);
      if (showQuestionPerformance) {
        loadQuizQuestionPerformance(selectedQuiz);
      }
    }
  }, [selectedQuiz, timeRange, showQuestionPerformance]);

  const loadQuizReport = async (quizId: number) => {
    try {
      setLoading(true);
      const response = await reportingAPI.getQuizDetailed(quizId, timeRange);
      setQuizReport(response.data.data);
    } catch (err) {
      console.error('Error loading quiz report:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadQuizQuestionPerformance = async (quizId: number) => {
    try {
      setLoadingQuestionPerformance(true);
      const response = await reportingAPI.getQuizQuestionPerformance(quizId, timeRange);
      setQuizQuestionPerformance(response.data.data);
    } catch (err) {
      console.error('Error loading quiz question performance:', err);
    } finally {
      setLoadingQuestionPerformance(false);
    }
  };

  return (
    <div className="quiz-reports-tab">
      <div className="quiz-selector">
        <label>Select Quiz:</label>
        <select 
          value={selectedQuiz || ''} 
          onChange={(e) => onQuizSelect(e.target.value ? parseInt(e.target.value) : null)}
        >
          <option value="">Choose a quiz...</option>
          {availableQuizzes
            .filter(quiz => quiz.has_data)
            .map(quiz => (
              <option key={quiz.id} value={quiz.id}>
                {quiz.title} ({quiz.completed_responses} responses)
              </option>
            ))}
        </select>
      </div>

      {loading && (
        <div className="loading-spinner">
          <div className="spinner-circle"></div>
          <div className="spinner-text">Loading quiz report...</div>
        </div>
      )}

      {quizReport && (
        <div className="quiz-report">
          <div className="report-header">
            <h3>{quizReport.quiz_title}</h3>
            <div className="quiz-stats-summary">
              <span>{quizReport.total_responses} total responses</span>
              <span>{quizReport.completed_responses} completed</span>
              <span>{Math.round(quizReport.average_score || 0)}% avg score</span>
            </div>
          </div>

          <div className="question-performance">
            <h4>Question Performance</h4>
            <div className="questions-list">
              {quizReport.question_stats.map((question: any, index: number) => (
                <div key={question.question_id} className="question-stat">
                  <div className="question-header">
                    <span className="question-number">Q{index + 1}</span>
                    <span className="question-text">{question.question_text}</span>
                  </div>
                  <div className="question-metrics">
                    <div className="metric">
                      <span className="metric-value">{question.accuracy_percentage}%</span>
                      <span className="metric-label">Accuracy</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{question.total_answers}</span>
                      <span className="metric-label">Answers</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{question.correct_answers}</span>
                      <span className="metric-label">Correct</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="actions">
            <button className="export-btn" onClick={() => onExport('quiz_detailed', selectedQuiz!)}>
              Export Quiz Report (PDF)
            </button>
            <button 
              className="export-btn" 
              onClick={() => {
                setShowQuestionPerformance(!showQuestionPerformance);
                if (!showQuestionPerformance && !quizQuestionPerformance) {
                  loadQuizQuestionPerformance(selectedQuiz!);
                }
              }}
            >
              {showQuestionPerformance ? 'Hide' : 'Show'} Question Performance Insights
            </button>
          </div>

          {showQuestionPerformance && (
            <QuizQuestionPerformanceSection
              performance={quizQuestionPerformance}
              loading={loadingQuestionPerformance}
              onExport={() => onExport('quiz_question_performance', selectedQuiz!)}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Category Performance Tab Component
const CategoryPerformanceTab: React.FC<{
  categories: CategoryPerformance[];
  onExport: (reportType: string, quizId?: number) => void;
}> = ({ categories, onExport }) => (
  <div className="categories-tab">
    <h3>Performance by Category</h3>
    <div className="categories-list">
      {categories.map((category, index) => (
        <div key={index} className="category-item">
          <div className="category-name">{category.category}</div>
          <div className="category-stats">
            <div className="category-stat">
              <span className="stat-value">{category.quiz_count}</span>
              <span className="stat-label">Quizzes</span>
            </div>
            <div className="category-stat">
              <span className="stat-value">{category.total_responses}</span>
              <span className="stat-label">Responses</span>
            </div>
            <div className="category-stat">
              <span className="stat-value">{Math.round(category.average_score)}%</span>
              <span className="stat-label">Avg Score</span>
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="actions">
      <button className="export-btn" onClick={() => onExport('category_performance')}>
        Export Category Report (PDF)
      </button>
    </div>
  </div>
);

// Question Performance Tab Component
const QuestionPerformanceTab: React.FC<{
  performance: OverallQuestionPerformance;
  onExport: (reportType: string, quizId?: number) => void;
}> = ({ performance, onExport }) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return '#28a745';
      case 'moderate':
        return '#ffc107';
      case 'difficult':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'Easy';
      case 'moderate':
        return 'Moderate';
      case 'difficult':
        return 'Difficult';
      default:
        return 'Unknown';
    }
  };

  // Sort topics by difficulty (difficult first)
  const sortedTopics = [...performance.topic_performance].sort((a, b) => {
    const difficultyOrder = { difficult: 0, moderate: 1, easy: 2 };
    return difficultyOrder[a.difficulty_level] - difficultyOrder[b.difficulty_level];
  });

  return (
    <div className="question-performance-tab">
      <div className="performance-header">
        <h2>Overall Question Performance Analysis</h2>
        <p className="performance-subtitle">
          Analyze where trainees are struggling across all tests to identify knowledge gaps
        </p>
      </div>

      {/* Overall Statistics */}
      <div className="overall-stats-section">
        <h3>Overall Statistics</h3>
        <div className="overall-stats-grid">
          <div className="overall-stat-card correct">
            <div className="stat-icon">✓</div>
            <div className="stat-content">
              <div className="stat-value">{performance.correct_percentage}%</div>
              <div className="stat-label">Correct Answers</div>
              <div className="stat-count">{performance.total_correct.toLocaleString()} of {performance.total_questions_answered.toLocaleString()}</div>
            </div>
          </div>
          <div className="overall-stat-card incorrect">
            <div className="stat-icon">✗</div>
            <div className="stat-content">
              <div className="stat-value">{performance.incorrect_percentage}%</div>
              <div className="stat-label">Wrong Answers</div>
              <div className="stat-count">{performance.total_incorrect.toLocaleString()} of {performance.total_questions_answered.toLocaleString()}</div>
            </div>
          </div>
          <div className="overall-stat-card partial">
            <div className="stat-icon">~</div>
            <div className="stat-content">
              <div className="stat-value">{performance.partial_percentage}%</div>
              <div className="stat-label">Partially Correct</div>
              <div className="stat-count">{performance.total_partial.toLocaleString()} of {performance.total_questions_answered.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Topic Performance */}
      <div className="topic-performance-section">
        <h3>Performance by Topic/Category</h3>
        <p className="section-description">
          Topics are sorted by difficulty level. Red indicates difficult topics where trainees struggle most.
        </p>
        
        <div className="topics-list">
          {sortedTopics.length > 0 ? (
            sortedTopics.map((topic, index) => (
              <div key={index} className={`topic-item ${topic.difficulty_level}`}>
                <div className="topic-header">
                  <div className="topic-name">{topic.category}</div>
                  <div 
                    className="difficulty-badge"
                    style={{ backgroundColor: getDifficultyColor(topic.difficulty_level) }}
                  >
                    {getDifficultyLabel(topic.difficulty_level)}
                  </div>
                </div>
                
                <div className="topic-stats-grid">
                  <div className="topic-stat">
                    <div className="topic-stat-value" style={{ color: '#28a745' }}>
                      {topic.correct_percentage}%
                    </div>
                    <div className="topic-stat-label">Correct</div>
                    <div className="topic-stat-count">{topic.correct_answers} answers</div>
                  </div>
                  
                  <div className="topic-stat">
                    <div className="topic-stat-value" style={{ color: '#dc3545' }}>
                      {topic.incorrect_percentage}%
                    </div>
                    <div className="topic-stat-label">Wrong</div>
                    <div className="topic-stat-count">{topic.incorrect_answers} answers</div>
                  </div>
                  
                  <div className="topic-stat">
                    <div className="topic-stat-value" style={{ color: '#ffc107' }}>
                      {topic.partial_percentage}%
                    </div>
                    <div className="topic-stat-label">Partial</div>
                    <div className="topic-stat-count">{topic.partial_answers} answers</div>
                  </div>
                  
                  <div className="topic-stat">
                    <div className="topic-stat-value" style={{ color: '#007bff' }}>
                      {topic.average_accuracy}%
                    </div>
                    <div className="topic-stat-label">Avg Accuracy</div>
                    <div className="topic-stat-count">{topic.total_questions_answered} total</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="topic-progress-bar">
                  <div 
                    className="progress-segment correct" 
                    style={{ width: `${topic.correct_percentage}%` }}
                    title={`${topic.correct_percentage}% Correct`}
                  ></div>
                  <div 
                    className="progress-segment partial" 
                    style={{ width: `${topic.partial_percentage}%` }}
                    title={`${topic.partial_percentage}% Partial`}
                  ></div>
                  <div 
                    className="progress-segment incorrect" 
                    style={{ width: `${topic.incorrect_percentage}%` }}
                    title={`${topic.incorrect_percentage}% Wrong`}
                  ></div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data-message">
              <p>No question performance data available for the selected time range.</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary Insights */}
      {sortedTopics.length > 0 && (
        <div className="insights-section">
          <h3>Key Insights</h3>
          <div className="insights-grid">
            <div className="insight-card">
              <h4>Most Difficult Topics</h4>
              <ul>
                {sortedTopics
                  .filter(t => t.difficulty_level === 'difficult')
                  .slice(0, 3)
                  .map((topic, idx) => (
                    <li key={idx}>
                      <strong>{topic.category}</strong> - {topic.average_accuracy}% accuracy
                    </li>
                  ))}
                {sortedTopics.filter(t => t.difficulty_level === 'difficult').length === 0 && (
                  <li>No topics marked as difficult</li>
                )}
              </ul>
            </div>
            
            <div className="insight-card">
              <h4>Easiest Topics</h4>
              <ul>
                {sortedTopics
                  .filter(t => t.difficulty_level === 'easy')
                  .slice(0, 3)
                  .map((topic, idx) => (
                    <li key={idx}>
                      <strong>{topic.category}</strong> - {topic.average_accuracy}% accuracy
                    </li>
                  ))}
                {sortedTopics.filter(t => t.difficulty_level === 'easy').length === 0 && (
                  <li>No topics marked as easy</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="actions">
        <button className="export-btn" onClick={() => onExport('overall_question_performance')}>
          Export Performance Report
        </button>
      </div>
    </div>
  );
};

// Quiz Question Performance Section Component
const QuizQuestionPerformanceSection: React.FC<{
  performance: QuizQuestionPerformance | null;
  loading: boolean;
  onExport: () => void;
}> = ({ performance, loading, onExport }) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '#28a745';
      case 'moderate': return '#ffc107';
      case 'difficult': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'Easy';
      case 'moderate': return 'Moderate';
      case 'difficult': return 'Difficult';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-circle"></div>
        <div className="spinner-text">Loading question performance insights...</div>
      </div>
    );
  }

  if (!performance) {
    return <div className="no-data-message">No question performance data available.</div>;
  }

  const sortedQuestions = [...performance.question_performance].sort((a, b) => {
    const difficultyOrder = { difficult: 0, moderate: 1, easy: 2 };
    return difficultyOrder[a.difficulty_level] - difficultyOrder[b.difficulty_level];
  });

  return (
    <div className="quiz-question-performance-section">
      <div className="performance-header">
        <h3>Question Performance Insights for {performance.quiz_title}</h3>
        <p className="performance-subtitle">
          Detailed analysis of individual questions to identify which questions are most challenging
        </p>
      </div>

      {/* Overall Statistics */}
      <div className="overall-stats-section">
        <h4>Overall Statistics</h4>
        <div className="overall-stats-grid">
          <div className="overall-stat-card correct">
            <div className="stat-icon">✓</div>
            <div className="stat-content">
              <div className="stat-value">{performance.correct_percentage}%</div>
              <div className="stat-label">Correct Answers</div>
              <div className="stat-count">{performance.total_correct.toLocaleString()} of {performance.total_questions_answered.toLocaleString()}</div>
            </div>
          </div>
          <div className="overall-stat-card incorrect">
            <div className="stat-icon">✗</div>
            <div className="stat-content">
              <div className="stat-value">{performance.incorrect_percentage}%</div>
              <div className="stat-label">Wrong Answers</div>
              <div className="stat-count">{performance.total_incorrect.toLocaleString()} of {performance.total_questions_answered.toLocaleString()}</div>
            </div>
          </div>
          <div className="overall-stat-card partial">
            <div className="stat-icon">~</div>
            <div className="stat-content">
              <div className="stat-value">{performance.partial_percentage}%</div>
              <div className="stat-label">Partially Correct</div>
              <div className="stat-count">{performance.total_partial.toLocaleString()} of {performance.total_questions_answered.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Performance */}
      <div className="question-performance-section">
        <h4>Performance by Question</h4>
        <p className="section-description">
          Questions are sorted by difficulty level. Red indicates difficult questions where trainees struggle most.
        </p>
        
        <div className="questions-list">
          {sortedQuestions.length > 0 ? (
            sortedQuestions.map((question) => (
              <div key={question.question_id} className={`question-item ${question.difficulty_level}`}>
                <div className="question-header">
                  <div className="question-number">Q{question.question_order}</div>
                  <div className="question-text">{question.question_text}</div>
                  <div 
                    className="difficulty-badge"
                    style={{ backgroundColor: getDifficultyColor(question.difficulty_level) }}
                  >
                    {getDifficultyLabel(question.difficulty_level)}
                  </div>
                </div>
                
                <div className="question-stats-grid">
                  <div className="question-stat">
                    <div className="question-stat-value" style={{ color: '#28a745' }}>
                      {question.correct_percentage}%
                    </div>
                    <div className="question-stat-label">Correct</div>
                    <div className="question-stat-count">{question.correct_answers} answers</div>
                  </div>
                  
                  <div className="question-stat">
                    <div className="question-stat-value" style={{ color: '#dc3545' }}>
                      {question.incorrect_percentage}%
                    </div>
                    <div className="question-stat-label">Wrong</div>
                    <div className="question-stat-count">{question.incorrect_answers} answers</div>
                  </div>
                  
                  <div className="question-stat">
                    <div className="question-stat-value" style={{ color: '#ffc107' }}>
                      {question.partial_percentage}%
                    </div>
                    <div className="question-stat-label">Partial</div>
                    <div className="question-stat-count">{question.partial_answers} answers</div>
                  </div>
                  
                  <div className="question-stat">
                    <div className="question-stat-value" style={{ color: '#007bff' }}>
                      {question.average_accuracy}%
                    </div>
                    <div className="question-stat-label">Avg Accuracy</div>
                    <div className="question-stat-count">{question.total_answers} total</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="question-progress-bar">
                  <div 
                    className="progress-segment correct" 
                    style={{ width: `${question.correct_percentage}%` }}
                    title={`${question.correct_percentage}% Correct`}
                  ></div>
                  <div 
                    className="progress-segment partial" 
                    style={{ width: `${question.partial_percentage}%` }}
                    title={`${question.partial_percentage}% Partial`}
                  ></div>
                  <div 
                    className="progress-segment incorrect" 
                    style={{ width: `${question.incorrect_percentage}%` }}
                    title={`${question.incorrect_percentage}% Wrong`}
                  ></div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data-message">
              <p>No question performance data available for the selected time range.</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary Insights */}
      {sortedQuestions.length > 0 && (
        <div className="insights-section">
          <h4>Key Insights</h4>
          <div className="insights-grid">
            <div className="insight-card">
              <h5>Most Difficult Questions</h5>
              <ul>
                {sortedQuestions
                  .filter(q => q.difficulty_level === 'difficult')
                  .slice(0, 5)
                  .map((question, idx) => (
                    <li key={idx}>
                      <strong>Q{question.question_order}</strong> - {question.average_accuracy}% accuracy
                      <br />
                      <span className="question-preview">{question.question_text.substring(0, 60)}...</span>
                    </li>
                  ))}
                {sortedQuestions.filter(q => q.difficulty_level === 'difficult').length === 0 && (
                  <li>No questions marked as difficult</li>
                )}
              </ul>
            </div>
            
            <div className="insight-card">
              <h5>Easiest Questions</h5>
              <ul>
                {sortedQuestions
                  .filter(q => q.difficulty_level === 'easy')
                  .slice(0, 5)
                  .map((question, idx) => (
                    <li key={idx}>
                      <strong>Q{question.question_order}</strong> - {question.average_accuracy}% accuracy
                      <br />
                      <span className="question-preview">{question.question_text.substring(0, 60)}...</span>
                    </li>
                  ))}
                {sortedQuestions.filter(q => q.difficulty_level === 'easy').length === 0 && (
                  <li>No questions marked as easy</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="actions">
        <button className="export-btn" onClick={onExport}>
          Export Question Performance Report
        </button>
      </div>
    </div>
  );
};

export default ReportsDashboardPage;
